import { AmplifyEventBus } from 'aws-amplify-vue';
import {
  CognitoUserPool,
  CognitoUserAttribute,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  NodeCallback,
  ISignUpResult,
} from 'amazon-cognito-identity-js';
 
import BaseAuth from '@/services/auth/Base';
 
import SignUpArgs from '@/entities/SignUpArgs';
import AuthenticatedUserOutput from '@/entities/AuthenticatedUserOutput';
import SignInOutput from '@/entities/SignInOutput';
import UserAuth from '@/entities/UserAuth';
import axios from 'axios';
 
interface PoolData {
  UserPoolId: string;
  ClientId: string;
}
 
interface UserData {
  UserStatus: 'CONFIRMED' | 'UNCONFIRMED' | 'FORCE_CHANGE_PASSWORD';
  Username: string; //user id
  ConfirmationCode?: string;
}
 
export default class DevAuth extends BaseAuth implements UserAuth {
  private userPool: CognitoUserPool;
  private cognitoUser?: CognitoUser = undefined;
  private poolData: PoolData = {
    UserPoolId: 'us-east-1_JEEhZV5jk',
    ClientId: '3h1thbf0400boie6gt8kcf0a9i',
  };
  private async getUserData(email: string): Promise<UserData> {
    const { ClientId } = this.poolData;
    const requestData = { ClientId, Username: email };
    const userData = await axios.post(this.endpoint, requestData, {
      headers: {
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.GetUser',
        'X-Amz-User-Agent': 'aws-amplify/0.1.x js',
        'Content-Type': 'application/x-amz-json-1.1',
      },
    });
    return userData.data;
  }
 
  constructor(private endpoint: string) {
    super();
    this.userPool = new CognitoUserPool({
      ...this.poolData,
      endpoint,
    });
  }
 
  protected getAuthenticatedUser(): Promise<AuthenticatedUserOutput> {
    return new Promise((resolve, reject) => {
      const cognitoUser = this.userPool.getCurrentUser();
 
      if (!cognitoUser) {
        return resolve({
          token: '',
          id: '',
        });
      }
 
      return cognitoUser.getSession(
        (error: Error | null, session: CognitoUserSession): void => {
          if (error) {
            throw reject(error.message);
          }
 
          const sessionUser = session.getIdToken();
 
          resolve({
            token: sessionUser.getJwtToken(),
            id: sessionUser.decodePayload().sub,
          });
        }
      );
    });
  }
 
  async signUp(cred: SignUpArgs): Promise<void> {
    const { username, password, attributes, clientMetadata } = cred;
 
    const attributeEmail = new CognitoUserAttribute({
      Name: 'email',
      Value: username,
    });
 
    const setSignUpResult: NodeCallback<Error, ISignUpResult> = (
      error,
      result
    ) => {
      if (error) {
        throw error.message;
      }
 
      AmplifyEventBus.$emit('localUser', result!.user);
    };
 
    this.userPool.signUp(
      username,
      password,
      [attributeEmail],
      [],
      setSignUpResult,
      {
        ...attributes,
        ...clientMetadata,
      }
    );
  }
 
  signIn(email: string, password: string): Promise<SignInOutput> {
    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      });
      const userData = {
        Username: email,
        Pool: this.userPool,
      };
      this.cognitoUser = new CognitoUser(userData);
 
      this.cognitoUser.setAuthenticationFlowType('USER_SRP_AUTH');
 
      return this.cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: async (result) => {
          const username = result
            .getAccessToken()
            .decodePayload().username;
          const user = await this.getUserData(email);
          if (user.UserStatus === 'FORCE_CHANGE_PASSWORD') {
            return resolve({
              username,
              challengeName: 'NEW_PASSWORD_REQUIRED',
              confirmationCode: user.ConfirmationCode,
            });
          }
 
          resolve({
            username,
            challengeName: user.UserStatus,
          });
        },
        onFailure: (err) => {
          if (err.code === 'UserNotConfirmedException') {
            throw reject({
              ...err,
              message:
                'This email address has not been confirmed yet, please check your inbox for an email from ProveraCare to verify your account.',
            });
          }
          if (err.code === 'NotAuthorizedException') {
            throw reject({
              ...err,
              message: 'Incorrect username or password.',
            });
          }
          throw reject(err);
        },
      });
    });
  }
 
  async signOut(): Promise<void> {
    if (this.cognitoUser) {
      this.cognitoUser.signOut();
    }
 
    AmplifyEventBus.$emit('authState', 'signedOut');
    // localStorage.clear();
    let lastAuthUser = localStorage.getItem(
      'CognitoIdentityServiceProvider.local_client_id.LastAuthUser'
    );
    localStorage.removeItem(
      'CognitoIdentityServiceProvider.local_client_id.' +
        lastAuthUser +
        '.clockDrift'
    );
    localStorage.removeItem(
      'CognitoIdentityServiceProvider.local_client_id.' +
        lastAuthUser +
        '.idToken'
    );
    localStorage.removeItem(
      'CognitoIdentityServiceProvider.local_client_id.' +
        lastAuthUser +
        '.accessToken'
    );
    localStorage.removeItem(
      'CognitoIdentityServiceProvider.local_client_id.' +
        lastAuthUser +
        '.refreshToken'
    );
    localStorage.removeItem(
      'CognitoIdentityServiceProvider.local_client_id.LastAuthUser'
    );
    localStorage.removeItem('intercom.intercom-state-hixuxmld');
  }
 
  confirmSignUp(email: string, code: string): Promise<void> {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: this.userPool,
    });
 
    return new Promise((resolve, reject) => {
      cognitoUser.confirmRegistration(code, true, (err, result) => {
        if (err) {
          return reject(err);
        }
        resolve(result);
      });
    });
  }
 
  async completeNewPassword(user: any, newPassword: string) {
    const { confirmationCode } = user;
    const cognitoUser = new CognitoUser({
      Username: user.username,
      Pool: this.userPool,
    });
    cognitoUser.confirmPassword(confirmationCode, newPassword, {
      onSuccess: async () => {
        this.signOut();
      },
      onFailure: (err: Error) => {
        throw err;
      },
    });
  }
 
  forgotPassword(username: string) {
    return Promise.resolve();
  }
 
  confirmForgotPassword(
    username: string,
    code: string,
    newPassword: string
  ): Promise<void> {
    return Promise.resolve();
  }
 
  changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<string> {
    return Promise.resolve().then(() => '');
  }
}
 